import type Phaser from 'phaser';
import { PALETTE } from './palette';

/**
 * プレイヤーのアオウミガメ。
 *
 * 円 1 つでは「泳いでいる」感じが出ないので、甲羅・頭・4 枚のヒレを持たせ、
 * 前ヒレを羽ばたかせる。アオウミガメは前ヒレを左右同時に打つので、
 * 交互ではなく揃えて動かすのが本物の泳ぎ方になる。
 *
 * すべての座標を回転して描くようにしてある。巻かれたときにもみくちゃに
 * 回すためで、楕円の描画命令では回せないので甲羅も多角形で作る。
 */

export interface TurtleOptions {
  /** 泳ぎの位相。前ヒレの羽ばたきに使う。 */
  readonly strokePhase: number;
  /** ラジアン。0 が浜向き（画面上）。 */
  readonly rotation: number;
  /** 0（水面）〜 1（潜行中）。潜るとヒレをたたむ。 */
  readonly submerged: number;
  readonly alpha: number;
  /** 単色で描く（ゴースト用）。 */
  readonly monotone?: number;
  readonly scale?: number;
}

/** ヒレ 1 枚。付け根から先端へ細くなる櫂の形。 */
function blade(
  g: Phaser.GameObjects.Graphics,
  map: (lx: number, ly: number) => { x: number; y: number },
  ox: number,
  oy: number,
  angleDeg: number,
  length: number,
  width: number,
): void {
  const a = (angleDeg * Math.PI) / 180;
  const px = Math.cos(a);
  const py = Math.sin(a);
  const nx = -py;
  const ny = px;

  const points = [
    [ox + nx * width * 0.5, oy + ny * width * 0.5],
    [ox + px * length * 0.55 + nx * width * 0.62, oy + py * length * 0.55 + ny * width * 0.62],
    [ox + px * length, oy + py * length],
    [ox + px * length * 0.5 - nx * width * 0.4, oy + py * length * 0.5 - ny * width * 0.4],
    [ox - nx * width * 0.5, oy - ny * width * 0.5],
  ] as const;

  g.beginPath();
  points.forEach(([lx, ly], i) => {
    const p = map(lx, ly);
    if (i === 0) g.moveTo(p.x, p.y);
    else g.lineTo(p.x, p.y);
  });
  g.closePath();
  g.fillPath();
}

/** 楕円を多角形で。回転させたいので描画命令の楕円は使わない。 */
function oval(
  g: Phaser.GameObjects.Graphics,
  map: (lx: number, ly: number) => { x: number; y: number },
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  segments = 18,
): void {
  g.beginPath();
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    const p = map(cx + Math.cos(t) * rx, cy + Math.sin(t) * ry);
    if (i === 0) g.moveTo(p.x, p.y);
    else g.lineTo(p.x, p.y);
  }
  g.closePath();
}

export function drawTurtle(
  g: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  options: TurtleOptions,
): void {
  const { strokePhase, rotation, submerged, alpha } = options;
  const scale = options.scale ?? 1;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const map = (lx: number, ly: number) => ({
    x: x + (lx * cos - ly * sin) * scale,
    y: y + (lx * sin + ly * cos) * scale,
  });

  const skin = options.monotone ?? PALETTE.turtleSkin;
  const skinDark = options.monotone ?? PALETTE.turtleSkinDark;
  const shell = options.monotone ?? PALETTE.shellBack;
  const shellLight = options.monotone ?? PALETTE.shellBackLight;
  const shellDark = options.monotone ?? PALETTE.shellBackDark;

  // 潜るとヒレをたたみ、全体が少し小さく見える。
  const spread = 1 - submerged * 0.45;
  const sweep = Math.sin(strokePhase) * spread;

  // ── 後ろヒレ。舵として左右逆に振る ──
  g.fillStyle(skinDark, alpha);
  blade(g, map, -7, 12, 118 + sweep * 10, 11 * spread, 6);
  blade(g, map, 7, 12, 62 - sweep * 10, 11 * spread, 6);

  // ── 前ヒレ。左右同時に打つ ──
  // 休めの角度を斜めにしておく。水平だと左右がつながって一直線の棒に見える。
  g.fillStyle(skin, alpha);
  blade(g, map, -10, -5, 163 + sweep * 26, 20 * spread, 7.5);
  blade(g, map, 10, -5, 17 - sweep * 26, 20 * spread, 7.5);
  // ヒレの縁。海の色に沈まないよう輪郭を付ける。
  g.fillStyle(skinDark, alpha * 0.35);
  blade(g, map, -10, -5, 163 + sweep * 26, 20 * spread, 3.2);
  blade(g, map, 10, -5, 17 - sweep * 26, 20 * spread, 3.2);

  // ── 頭 ──
  const headOffset = -20 + submerged * 1.5;
  g.fillStyle(skin, alpha);
  oval(g, map, 0, headOffset, 6, 7, 12);
  g.fillPath();
  // 目。小さくても向きが伝わる。
  g.fillStyle(shellDark, alpha * 0.9);
  const eyeL = map(-2.9, headOffset - 1.6);
  const eyeR = map(2.9, headOffset - 1.6);
  g.fillCircle(eyeL.x, eyeL.y, 1.2 * scale);
  g.fillCircle(eyeR.x, eyeR.y, 1.2 * scale);

  // ── 甲羅 ──
  g.fillStyle(shell, alpha);
  oval(g, map, 0, 0, 13, 17);
  g.fillPath();

  // 甲板。中央列と、そこから縁へ伸びる線で分ける。
  g.lineStyle(1.3 * scale, shellDark, alpha * 0.55);
  oval(g, map, 0, 0, 6.6, 11, 14);
  g.strokePath();
  for (const ly of [-7, 0, 7]) {
    for (const side of [-1, 1]) {
      const a = map(side * 6.4, ly);
      const b = map(side * 12.4, ly * 0.82);
      g.beginPath();
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
      g.strokePath();
    }
  }
  for (const ly of [-5.5, 5.5]) {
    const a = map(-6.2, ly);
    const b = map(6.2, ly);
    g.beginPath();
    g.moveTo(a.x, a.y);
    g.lineTo(b.x, b.y);
    g.strokePath();
  }

  // 甲羅の縁と、左上からの照り。太陽の位置と合わせてある。
  g.lineStyle(1.6 * scale, shellDark, alpha * 0.8);
  oval(g, map, 0, 0, 13, 17);
  g.strokePath();
  g.fillStyle(shellLight, alpha * 0.4);
  oval(g, map, -4, -6, 4.6, 6.4, 12);
  g.fillPath();
}
